import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAudience } from '../context/AudienceContext';
import { Switch } from './ui/switch';
import JuliaVoiceAssistant from './JuliaVoiceAssistant';
import AIInstantButton from './AIInstantButton';
import { 
  Stethoscope, 
  Home, 
  Pill, 
  Calculator, 
  FileText, 
  MessageCircle, 
  ShieldAlert,
  Search,
  Baby,
  Shield,
  Users,
  Syringe,
  Activity,
  Wrench,
  ChevronDown,
  ChevronRight,
  X,
  HeartPulse,
  Brain,
  Bone,
  Heart,
  Zap,
  Wind,
  Droplet,
  AlertTriangle,
  Skull
} from 'lucide-react';

const mainNavItems = [
  { path: '/', label: 'Accueil', icon: Home },
  { path: '/chat', label: 'Chat IA', icon: MessageCircle },
  { path: '/protocols', label: 'Protocoles', icon: FileText },
];

const toolsItems = [
  { path: '/medications', label: 'Médicaments', icon: Pill },
  { path: '/medicaments-adulte', label: 'Médicaments Adulte', icon: Pill },
  { path: '/medicaments-pediatrie', label: 'Médicaments Pédiatrie', icon: Baby },
  { path: '/prescription-adulte', label: 'Prescrire Adulte', icon: Pill },
  { path: '/prescription-pediatrie', label: 'Prescrire Pédiatrie', icon: Baby },
  { path: '/infections', label: 'Infections', icon: Shield },
  { path: '/gaz-du-sang', label: 'Gaz du Sang', icon: Droplet },
  { path: '/lcr', label: 'LCR (Méningites)', icon: Brain },
  { path: '/ponction-lombaire', label: 'Ponction Lombaire', icon: Syringe },
  { path: '/antibio', label: 'Antibio', icon: Syringe },
  { path: '/calculator', label: 'Calculateurs', icon: Calculator },
  { path: '/scores', label: 'Scores', icon: Activity },
  { path: '/ramsay-score', label: 'Score Ramsay', icon: Brain },
  { path: '/diagnostic', label: 'Diagnostic', icon: HeartPulse },
  { path: '/eruptive-diseases', label: 'Éruptions', icon: Baby },
  { path: '/avc-expert', label: 'AVC (Expert)', icon: Brain },
  { path: '/cardio-ref', label: 'Référentiels Cardio', icon: Heart },
  { path: '/circonstanciel-ref', label: 'Référentiels Circonstanciel', icon: AlertTriangle },
  { path: '/pediatrie-ref', label: 'Référentiels Pédiatrie-Néonat', icon: Baby },
  { path: '/neuro-ref', label: 'Référentiels Neurologie', icon: Brain },
  { path: '/intox-ref', label: 'Référentiels Intox Médic', icon: Skull },
  { path: '/metabolique-ref', label: 'Référentiels Métabolique', icon: Activity },
  { path: '/trauma-ref', label: 'Référentiels Trauma', icon: Shield },
  { path: '/infectio-ref', label: 'Référentiels Infectiologie', icon: ShieldAlert },
  { path: '/pneumo-ref', label: 'Référentiels Pneumologie', icon: Wind },
  { path: '/gastro-ref', label: 'Référentiels Gastro-E', icon: Droplet },
  { path: '/urg-rythmo', label: "Urg'Rythmologie", icon: Activity },
  { path: '/sca-st-plus', label: 'SCA ST+', icon: Heart },
  { path: '/nstemi', label: 'NSTEMI', icon: Heart },
  { path: '/ees', label: 'EES', icon: Zap },
  { path: '/vni', label: 'VNI', icon: Wind },
  { path: '/vm', label: 'VM', icon: Wind },
  { path: '/modes-ventilatoires', label: 'Modes Ventilatoires', icon: Wind },
  { path: '/tamponnade', label: 'Tamponnade Cardiaque', icon: Heart },
  { path: '/ponction-pericardique', label: 'Ponction Péricardique', icon: Syringe },
  { path: '/parametres-enfant', label: 'Paramètres & Matériel', icon: Baby },
  { path: '/noradrenaline', label: 'Noradrénaline', icon: Droplet },
  { path: '/lactate-sodium', label: 'Lactate de sodium 112‰', icon: Droplet },
  { path: '/voie-nasale', label: 'Voie Nasale', icon: Droplet },
  { path: '/ecg-ai', label: 'ECG AI', icon: Activity },
  { path: '/smur', label: 'SMUR', icon: Activity },
  { 
    label: 'Technique', 
    icon: Wrench, 
    isSubmenu: true,
    items: [
      { path: '/intubation', label: 'Intubation', icon: Syringe },
      { path: '/intubation-pediatric', label: 'Intubation Pédiatrique', icon: Baby },
      { path: '/sedation-procedurale', label: 'Sédation Procédurale', icon: Syringe },
      { path: '/drainage-thoracique', label: 'Drainage Thoracique', icon: Activity },
      { path: '/trauma-severe', label: 'Trauma Sévère', icon: Activity },
      { path: '/marche-checklist', label: 'MARCHE Trauma', icon: Activity },
      { path: '/accouchement-aide', label: 'Aide Accouchement', icon: Baby },
      { path: '/voie-intraosseuse', label: 'Voie Intraosseuse', icon: Syringe },
      { path: '/traumato-urgences', label: 'Traumato Urgences', icon: Bone }
    ]
  },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { audience, setAudience, isAdult } = useAudience();
  const [toolsOpen, setToolsOpen] = useState(true);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);

  // Check if current path is in tools
  const isToolsActive = toolsItems.some(item => location.pathname === item.path);

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] grid-background">
      {/* Header */}
      <header className="sticky top-0 z-40 hero-gradient backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="container mx-auto px-4 flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="p-1.5 rounded-lg bg-cyan-400/20 glow-cyan">
              <Stethoscope className="w-5 h-5 text-cyan-300" />
            </div>
            <span className="font-semibold text-lg glow-text hidden sm:inline">UrgenceMed</span>
          </Link>

          {/* Center - Audience Toggle */}
          <div className="flex items-center gap-3 bg-[hsl(var(--bg-elev))] rounded-lg px-3 py-1.5 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-1.5">
              <Users className={`w-4 h-4 ${isAdult ? 'text-cyan-300' : 'text-[hsl(var(--text-dim))]'}`} />
              <span className={`text-xs font-medium ${isAdult ? 'text-cyan-300' : 'text-[hsl(var(--text-dim))]'}`}>Adulte</span>
            </div>
            <Switch 
              checked={!isAdult}
              onCheckedChange={() => setAudience(isAdult ? 'pediatric' : 'adult')}
              data-testid="age-toggle-switch"
              aria-label="Basculer adulte/pédiatrique"
            />
            <div className="flex items-center gap-1.5">
              <Baby className={`w-4 h-4 ${!isAdult ? 'text-cyan-300' : 'text-[hsl(var(--text-dim))]'}`} />
              <span className={`text-xs font-medium ${!isAdult ? 'text-cyan-300' : 'text-[hsl(var(--text-dim))]'}`}>Pédiatrique</span>
            </div>
          </div>

          {/* Right - Placeholder for future features */}
          <div className="flex items-center gap-3">
            {/* Section removed: Open access model */}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:flex flex-col w-64 min-h-[calc(100vh-56px)] border-r border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))/50]">
          <nav className="flex-1 p-4 space-y-1">
            {/* Main Navigation Items */}
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* Tools Dropdown */}
            <div className="pt-2">
              <button
                onClick={() => setToolsOpen(!toolsOpen)}
                className={`nav-item w-full justify-between ${isToolsActive ? 'active' : ''}`}
                data-testid="nav-tools"
              >
                <div className="flex items-center gap-3">
                  <Wrench className="w-5 h-5" />
                  <span>Outils</span>
                </div>
                {toolsOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {toolsOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l border-[hsl(var(--border))] pl-3">
                  {toolsItems.map((item) => {
                    // Handle submenu items
                    if (item.isSubmenu) {
                      const Icon = item.icon;
                      return (
                        <div key={item.label} className="space-y-1">
                          <div className="nav-item text-sm opacity-70 cursor-default">
                            <Icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </div>
                          <div className="ml-4 space-y-1">
                            {item.items.map((subItem) => {
                              const SubIcon = subItem.icon;
                              const isActive = location.pathname === subItem.path;
                              return (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  className={`nav-item text-xs ${isActive ? 'active' : ''}`}
                                  data-testid={`nav-${subItem.path.replace('/', '')}`}
                                >
                                  <SubIcon className="w-3.5 h-3.5" />
                                  <span>{subItem.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    
                    // Handle regular items
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`nav-item text-sm ${isActive ? 'active' : ''}`}
                        data-testid={`nav-${item.path.replace('/', '')}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>
          
          {/* Version */}
          <div className="p-4 border-t border-[hsl(var(--border))]">
            <p className="text-xs text-[hsl(var(--muted-2))]">
              UrgenceMed v1.0.0
            </p>
            <p className="text-xs text-[hsl(var(--muted-2))]">
              Mode: <span className="text-cyan-400 font-medium">{isAdult ? 'Adulte' : 'Pédiatrique'}</span>
            </p>
            <p className="text-xs text-[hsl(var(--muted-2))] mt-2">
              © 2026 OpenMeta. Tous droits réservés.
            </p>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 main-content pb-16 md:pb-8">
          {children}
          {/* Footer - visible on main content area */}
          <footer className="hidden md:block text-center py-4 mt-8 border-t border-[hsl(var(--border))]">
            <p className="text-xs text-[hsl(var(--muted-2))]">
              © 2026 OpenMeta. Tous droits réservés.
            </p>
          </footer>
        </main>
      </div>

      {/* Footer - Mobile (above bottom nav) */}
      <div className="md:hidden text-center py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))]" style={{ marginBottom: '60px' }}>
        <p className="text-xs text-[hsl(var(--muted-2))]">
          © 2026 OpenMeta. Tous droits réservés.
        </p>
      </div>

      {/* Bottom Nav - Mobile */}
      <nav className="bottom-nav md:hidden">
        <Link
          to="/"
          className={`bottom-nav-item ${location.pathname === '/' ? 'active' : ''}`}
        >
          <Home className="w-5 h-5" />
          <span>Accueil</span>
        </Link>
        <Link
          to="/chat"
          className={`bottom-nav-item ${location.pathname === '/chat' ? 'active' : ''}`}
        >
          <MessageCircle className="w-5 h-5" />
          <span>Chat IA</span>
        </Link>
        <Link
          to="/protocols"
          className={`bottom-nav-item ${location.pathname === '/protocols' ? 'active' : ''}`}
        >
          <FileText className="w-5 h-5" />
          <span>Protocoles</span>
        </Link>
        <button
          onClick={() => setMobileToolsOpen(true)}
          className={`bottom-nav-item ${isToolsActive ? 'active' : ''}`}
        >
          <Wrench className="w-5 h-5" />
          <span>Outils</span>
        </button>
      </nav>

      {/* Mobile Tools Menu */}
      {mobileToolsOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileToolsOpen(false)}
          />
          {/* Menu */}
          <div className="absolute bottom-0 left-0 right-0 bg-[hsl(var(--bg-elev))] border-t border-[hsl(var(--border))] rounded-t-2xl p-4 pb-8 animate-in slide-in-from-bottom">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Wrench className="w-5 h-5 text-cyan-400" />
                Outils
              </h3>
              <button
                onClick={() => setMobileToolsOpen(false)}
                className="p-2 rounded-full hover:bg-[hsl(var(--border))]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
              {toolsItems.map((item) => {
                // Handle submenu items
                if (item.isSubmenu && item.items) {
                  const SubmenuIcon = item.icon;
                  return (
                    <div key={item.label} className="col-span-2 space-y-2">
                      <div className="flex items-center gap-2 px-3 py-2 bg-[hsl(var(--bg))] rounded-lg border border-[hsl(var(--border))]">
                        <SubmenuIcon className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-semibold text-cyan-400">{item.label}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pl-2">
                        {item.items.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileToolsOpen(false)}
                              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all ${
                                isActive
                                  ? 'bg-cyan-400/20 border-cyan-400/50 text-cyan-400'
                                  : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-cyan-400/30'
                              }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span className="text-[10px] font-medium text-center leading-tight">{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                // Handle regular items
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path || item.label}
                    to={item.path}
                    onClick={() => setMobileToolsOpen(false)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                      isActive
                        ? 'bg-cyan-400/20 border-cyan-400/50 text-cyan-400'
                        : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-cyan-400/30'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Julia Voice Assistant - Floating button accessible from all pages */}
      <JuliaVoiceAssistant />
      
      {/* AI Instant Button - Text-based AI assistant */}
      <AIInstantButton />

      {/* Footer */}
      <footer className="bg-[hsl(var(--bg-elev))] border-t border-[hsl(var(--border))] py-4 px-6 mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[hsl(var(--text-dim))]">
            © 2024 UrgenceMed - OpenMeta. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6 text-xs text-[hsl(var(--text-dim))]">
            <button 
              onClick={() => navigate('/guide')}
              className="hover:text-cyan-400 transition-colors"
            >
              Guide d&apos;utilisation
            </button>
            <span className="text-[hsl(var(--border))]">|</span>
            <button 
              onClick={() => navigate('/cgu')}
              className="hover:text-cyan-400 transition-colors"
            >
              Conditions d&apos;utilisation
            </button>
            <span className="text-[hsl(var(--border))]">|</span>
            <button 
              onClick={() => navigate('/privacy')}
              className="hover:text-cyan-400 transition-colors"
            >
              Politique de confidentialité
            </button>
            <span className="text-[hsl(var(--border))]">|</span>
            <a 
              href="mailto:support@openmeta.fr"
              className="hover:text-cyan-400 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
